<template>
  <!-- 与 Toolbar 一致：常挂载，靠 show 切换；避免 v-if+show=true 同时创建导致 Preview watch 不触发、画布空白 -->
  <Preview :show="innerShow" @change="onPreviewChange" />
</template>

<script>
import Preview from "@/components/Editor/Preview";
import eventBus from "@/utils/eventBus";
import {
  parseCanvasAllData,
  isPageSkeleton,
  normalizePageDataPayload,
  mergePageIntoCanvasAllData,
} from "@/utils/canvasPageLoader";
import {
  snapshotPreviewStore,
  restorePreviewStore,
  bootstrapProjectForPreview,
} from "@/utils/projectListPreview";

export default {
  name: "ProjectListPreview",
  components: { Preview },
  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: [String, Number], default: "" },
  },
  data() {
    return {
      innerShow: false,
      loading: false,
      storeSnap: null,
      activeProjectId: "",
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.openPreview();
      } else {
        this.closePreview(false);
      }
    },
  },
  beforeDestroy() {
    eventBus.$off("ensureCanvasPages", this.onEnsureCanvasPages);
    this.closePreview(false);
  },
  methods: {
    pickCanvasPageArray(canvasData, pageKey) {
      if (!canvasData || pageKey == null) {
        return null;
      }
      const s = String(pageKey);
      if (Object.prototype.hasOwnProperty.call(canvasData, s)) {
        return canvasData[s];
      }
      for (const k of Object.keys(canvasData)) {
        if (String(k) === s) {
          return canvasData[k];
        }
      }
      return null;
    },
    onEnsureCanvasPages(payload) {
      if (!this.innerShow || !this.activeProjectId) {
        if (payload && typeof payload.done === "function") {
          payload.done();
        }
        return;
      }
      const pageIds = (payload && payload.pageIds) || [];
      const done = payload && payload.done;
      this.ensurePagesLoaded(pageIds)
        .then(() => {
          if (typeof done === "function") {
            done();
          }
        })
        .catch(() => {
          if (typeof done === "function") {
            done();
          }
        });
    },
    async ensurePagesLoaded(pageIds) {
      const ids = (pageIds || []).map((id) => String(id));
      for (const id of ids) {
        await this.ensurePageLoaded(id);
      }
    },
    async ensurePageLoaded(pageId) {
      const key = String(pageId);
      const lazy = this.$store.state.canvasLazyLoad;
      if (!lazy) {
        return true;
      }
      const loaded = this.$store.state.canvasLoadedPageIds || {};
      const data = parseCanvasAllData(this.$store.state.canvasAllData);
      const existing = this.pickCanvasPageArray(data, key);
      if (loaded[key] && existing && !isPageSkeleton(existing)) {
        return true;
      }
      if (!this.activeProjectId) {
        return false;
      }
      const res = await this.$request.findProjectPage({
        id: this.activeProjectId,
        pageId: key,
      });
      if (!res || res.status !== "success") {
        this.$message.error((res && res.msg) || "页面加载失败");
        return false;
      }
      const pageArr = normalizePageDataPayload(
        res.data && (res.data.pageData != null ? res.data.pageData : res.data.page_data)
      );
      if (!pageArr.length || isPageSkeleton(pageArr)) {
        return false;
      }
      const merged = mergePageIntoCanvasAllData(
        this.$store.state.canvasAllData,
        key,
        pageArr
      );
      this.$store.commit("setCanvasAllData", merged);
      this.$store.commit("markCanvasPageLoaded", key);
      return true;
    },
    async openPreview() {
      const id = this.projectId;
      if (!id || this.loading) {
        return;
      }
      this.loading = true;
      const loadingInst = this.$loading({
        lock: true,
        text: this.$tt("加载中"),
        background: "rgba(0,0,0,0.35)",
      });
      this.storeSnap = snapshotPreviewStore(this.$store);
      eventBus.$on("ensureCanvasPages", this.onEnsureCanvasPages);
      try {
        const res = await this.$request.getProjectInfo({ id, lazyLoad: true });
        if (res.status !== "success") {
          this.$message.error(res.msg || "工程加载失败");
          this.$emit("close");
          return;
        }
        if (!res.data || !res.data.panel_data) {
          this.$message.warning("工程画布数据为空");
          this.$emit("close");
          return;
        }
        this.activeProjectId = String(id);
        bootstrapProjectForPreview(this.$store, res.data);
        this.innerShow = true;
      } catch (e) {
        this.$message.error("工程加载失败，请稍后重试");
        this.$emit("close");
      } finally {
        loadingInst.close();
        this.loading = false;
      }
    },
    onPreviewChange(val) {
      if (!val) {
        this.closePreview(true);
      }
    },
    closePreview(emitClose) {
      eventBus.$off("ensureCanvasPages", this.onEnsureCanvasPages);
      this.innerShow = false;
      this.activeProjectId = "";
      restorePreviewStore(this.$store, this.storeSnap);
      this.storeSnap = null;
      this.$store.commit("setEditMode", "edit");
      if (emitClose) {
        this.$emit("close");
      }
    },
  },
};
</script>
